import "../foodstuff-suggestions/FoodstuffSuggestions.css";
import "./UnverifiedFoodstuffs.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useState } from "react";

import foodstuffService from "../../services/foodstuff-service";
import { GlobalContext } from "../GlobalContext";
import { useLocation } from "react-router-dom";
import NumericPagination from "../../components/NumericPagination/NumericPagination";
import { ColumnHeader, ColumnValue, DefaultTableFooter } from "../../components/DefaultTable/DefaultTable";
import { formatToDayMonthYearDateTime } from "../../utils/date-converter";
import DefaultButton from "../../components/DefaultButton/DefaultButton";
import DefaultModal from "../../components/DefaultModal/DefaultModal";
import { useIsMobile } from "../../hooks/useMediaQuery";

const UnverifiedFoodstuffs = () => {
  const isMobile = useIsMobile();
  const [foodstuffsResponse, setFoodstuffsResponse] = useState({});
  const [foodstuffs, setFoodstuffs] = useState([]);
  const [currentPage, setCurrentPage] = useState();
  const searchParams = new URLSearchParams(useLocation().search);
  const pageParam = searchParams?.get("currentPage");
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);
  const [selectedFoodstuff, setSelectedFoodstuff] = useState(null);

  const columnsWidth = {
    NAME: 2.4,
    BRAND: 1.6,
    DATE: 1.8,
    CREATOR: 1.8,
    IMAGE: 0.9,
    STATUS: 1.3,
    ACTIONS: 1.2,
  };

  const fetchFoodstuffs = useCallback(
    async (page) => {
      if (page < 0) {
        fetchFoodstuffs(0);
        return;
      }

      setIsLoading(true);
      setCurrentPage(page + 1);
      try {
        const response = await foodstuffService.getUnverifiedFoodstuffs(page, 7, "");
        setFoodstuffsResponse(response);
        setFoodstuffs(response.content);

        if (page > response.total_pages) {
          fetchFoodstuffs(response.total_pages - 1);
        }
      } catch (error) {
        console.log("Erro ao buscar alimentos não verificados", error);
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading]
  );

  useEffect(() => {
    document.title = "Alimentos não verificados";
    setSelectedScreen("unverified-foodstuffs");
    const pageFromParam = pageParam ? Number(pageParam) - 1 : 0;
    fetchFoodstuffs(pageFromParam);
  }, [pageParam, fetchFoodstuffs, setSelectedScreen]);

  const nutrition = selectedFoodstuff?.nutrition_information || {};

  return (
    <>
      <div className="right-side">
        <div className="right-side__content">
          <div className="right-side__content__header">
            <h1>
              Alimentos não verificados{" "}
              {foodstuffsResponse?.total_elements ? `(${foodstuffsResponse?.total_elements})` : ""}
            </h1>
          </div>
          <div className="foodstuffSuggestions">
            <div className="foodstuffSuggestions__content">
              {foodstuffsResponse?.total_elements > 0 && (
                <div className="default-table foodstuffSuggestions__table">
                  {isMobile ? (
                    <div className="foodstuffSuggestions__mobile-list">
                      {foodstuffs.map((foodstuff) => (
                        <button
                          key={foodstuff.id}
                          type="button"
                          className="foodstuffSuggestion__mobile-row"
                          onClick={() => setSelectedFoodstuff(foodstuff)}
                        >
                          <div className="foodstuffSuggestion__mobile-row__info">
                            <p className="foodstuffSuggestion__mobile-row__name">{foodstuff.name}</p>
                            <p className="foodstuffSuggestion__mobile-row__brand">
                              {foodstuff.created_by_name || "—"}
                            </p>
                            <span className="foodstuffSuggestion__mobile-row__image-badge">Não verificado</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="default-table--preserve-rows foodstuffSuggestions__table__desktop">
                      <div className="foodstuffSuggestions__table__scroll">
                        <div>
                          <div className="table__header">
                            <ColumnHeader title="Nome" column="NAME" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Marca" column="BRAND" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Data" column="DATE" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Criado por" column="CREATOR" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Imagem" column="IMAGE" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Status" column="STATUS" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Ações" column="ACTIONS" columnsWidth={columnsWidth} />
                          </div>
                          <div className="table__content">
                            {foodstuffs.map((foodstuff) => (
                              <div className="table__row" key={foodstuff.id}>
                                <ColumnValue column="NAME" columnsWidth={columnsWidth}>
                                  {foodstuff.name}
                                </ColumnValue>
                                <ColumnValue
                                  column="BRAND"
                                  columnsWidth={columnsWidth}
                                  className={foodstuff?.brand?.name ? "" : "foodstuffSuggestion__brand--not-appliable"}
                                >
                                  {foodstuff?.brand?.name || "Não aplicável"}
                                </ColumnValue>
                                <ColumnValue column="DATE" columnsWidth={columnsWidth}>
                                  {formatToDayMonthYearDateTime(foodstuff.created_at)}
                                </ColumnValue>
                                <ColumnValue column="CREATOR" columnsWidth={columnsWidth}>
                                  {foodstuff.created_by_name || "—"}
                                </ColumnValue>
                                <ColumnValue
                                  column="IMAGE"
                                  columnsWidth={columnsWidth}
                                  className={foodstuff?.source_image?.url ? "" : "foodstuffSuggestion__brand--not-appliable"}
                                >
                                  {foodstuff?.source_image?.url ? "Sim" : "Não"}
                                </ColumnValue>
                                <ColumnValue column="STATUS" columnsWidth={columnsWidth}>
                                  <span className="unverified-foodstuffs__badge">Não verificado</span>
                                </ColumnValue>
                                <ColumnValue column="ACTIONS" columnsWidth={columnsWidth} className="foodstuffSuggestion__actions-column">
                                  <div className="foodstuffSuggestion__actions">
                                    <DefaultButton
                                      title="Ver detalhes"
                                      onClick={() => setSelectedFoodstuff(foodstuff)}
                                      size="small"
                                      textStyle="foodstuffSuggestion__action"
                                    />
                                  </div>
                                </ColumnValue>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <DefaultTableFooter>
                    <NumericPagination
                      currentPage={currentPage || 1}
                      totalPages={foodstuffsResponse.total_pages}
                      pagesPerView={isMobile ? 5 : 10}
                      onSelectPage={(page) => fetchFoodstuffs(page)}
                    />
                  </DefaultTableFooter>
                </div>
              )}
              {foodstuffsResponse?.total_elements === 0 && (
                <p className="unverified-foodstuffs__empty">Nenhum alimento não verificado no momento.</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <DefaultModal
        title={selectedFoodstuff?.name || "Alimento não verificado"}
        showModal={!!selectedFoodstuff}
        onClose={() => setSelectedFoodstuff(null)}
        className="unverified-foodstuffs__modal"
      >
        {selectedFoodstuff && (
          <div className="unverified-foodstuffs__detail">
            <p><strong>Criado por:</strong> {selectedFoodstuff.created_by_name || "—"}</p>
            <p><strong>Marca:</strong> {selectedFoodstuff.brand?.name || "Não aplicável"}</p>
            <p><strong>Status:</strong> Não verificado</p>
            <p><strong>Calorias:</strong> {nutrition.calories ?? 0}</p>
            <p><strong>Proteínas:</strong> {nutrition.proteins ?? 0} g</p>
            <p><strong>Carboidratos:</strong> {nutrition.carbohydrates ?? 0} g</p>
            <p><strong>Sódio:</strong> {nutrition.sodium ?? 0} mg</p>
            <p><strong>Fibras:</strong> {nutrition.fiber ?? 0} g</p>
            <p><strong>Gorduras:</strong> {nutrition.fats ?? 0} g</p>
            {selectedFoodstuff.source_image?.url ? (
              <img
                className="unverified-foodstuffs__detail__image"
                src={selectedFoodstuff.source_image.url}
                alt="Tabela nutricional enviada pelo usuário"
              />
            ) : (
              <p className="foodstuffSuggestion__brand--not-appliable">Sem imagem-fonte</p>
            )}
          </div>
        )}
      </DefaultModal>
    </>
  );
};

export default UnverifiedFoodstuffs;
